import * as React from 'react'
import { useDiagnosticsApi, useInvokeApi, useAuth } from '../../hooks'
import { Formik } from 'formik'
import {
  FormikSelect,
  EditModalActionBar,
  FormikTextField,
} from '../../components'
import { Form, Box, Loader } from 'cs-kevlar-react'
import { Option } from 'cs-kevlar-react/lib/es/Select'
import { Grid } from 'cs-kevlar-react'
import {
  SupportFederatedAuthenticationConfiguration,
  SupportUserPermission,
} from '../../api'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'

interface EditSupportFederatedAuthenticationConfigurationProps {
  onClose?: () => void
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'hidden',
      overflowX: 'hidden',
      padding: theme.spacing(8),
    },
    icon: {
      minWidth: '30px',
    },
  }),
)

const booleanOptions: Option[] = [
  {
    key: 'true',
    value: true,
    label: 'true',
  },
  {
    key: 'false',
    value: false,
    label: 'false',
  },
]

export const EditSupportFederatedAuthenticationConfiguration: React.FunctionComponent<EditSupportFederatedAuthenticationConfigurationProps> = ({
  onClose,
}) => {
  const {
    getSupportFederatedAuthenticationConfiguration,
    updateSupportFederatedAuthenticationConfiguration,
  } = useDiagnosticsApi()
  const classes = useStyles()
  const invokeApi = useInvokeApi()
  const { user: authUser } = useAuth()
  const [
    configuration,
    setConfiguration,
  ] = React.useState<SupportFederatedAuthenticationConfiguration | null>(null)

  React.useEffect(() => {
    invokeApi(
      () => getSupportFederatedAuthenticationConfiguration(),
      'There was an error getting the configuration',
      (result) => {
        setConfiguration(result)
      },
    )
  }, [])

  if (authUser === null) return null

  const onEdit = (
    values: SupportFederatedAuthenticationConfiguration,
    actions: any,
  ) => {
    invokeApi(
      () => updateSupportFederatedAuthenticationConfiguration(values),
      'There was an error updating the configuration',
      (_) => {},
      () => {
        if (onClose !== undefined) {
          onClose()
        }
      },
    )
  }

  const canEdit =
    authUser.isSupport &&
    authUser.permissions.includes(
      SupportUserPermission.ManageDiagnosticConfiguration,
    )

  return (
    <React.Fragment>
      {configuration === null && (
        <Loader size="sm" text="Loading Configuration" />
      )}
      {configuration !== null && (
        <Formik initialValues={configuration} onSubmit={onEdit}>
          {(formikProps) => (
            <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
              <Box className={classes.formFieldContainer}>
                <Grid container direction="column">
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      id="clientId"
                      fieldName="clientId"
                      label="Client ID"
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      id="authorizeEndpoint"
                      fieldName="authorizeEndpoint"
                      label="Authorize Endpoint"
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      id="tokenEndpoint"
                      fieldName="tokenEndpoint"
                      label="Token Endpoint"
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      id="userInfoEndpoint"
                      fieldName="userInfoEndpoint"
                      label="User Info Endpoint"
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      id="logoutEndpoint"
                      fieldName="logoutEndpoint"
                      label="Logout Endpoint"
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      id="scope"
                      fieldName="scope"
                      label="Scope"
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      id="grantType"
                      fieldName="grantType"
                      label="Grant Type"
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikSelect
                      formikProps={formikProps}
                      id="supportsPkce"
                      fieldName="supportsPkce"
                      label="Supports PKCE"
                      options={booleanOptions}
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Grid item>
                    <FormikTextField
                      formikProps={formikProps}
                      id="tokenKeys"
                      fieldName="tokenKeys"
                      label="Tokens"
                      helperText="Comma separated"
                      fullWidth
                      disabled={!canEdit}
                    />
                  </Grid>
                  <Box className={classes.spacer} />
                </Grid>
              </Box>
              {
                <EditModalActionBar
                  canDelete={false}
                  onCancel={onClose}
                  canSubmit={canEdit}
                  formikProps={formikProps}
                />
              }
            </Form>
          )}
        </Formik>
      )}
    </React.Fragment>
  )
}
